import styled from '@emotion/styled';
import { Modal, ModalProps, Result, Button } from 'antd';
import Chat from 'views/Conversation/Chat';
import { ConversationSider } from 'views/Conversation/Sider';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { buyerNickMask } from 'services/channels/demo';
import useSecondConversation from 'hooks/business/useSecondConversation';
import { ChatConversationProvider } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { ChatErrorBoundary } from 'views/Conversation/components/ChatSection';
import { LightChatSide } from 'components/LightConversation/LightChatSide';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { LightChatHeader } from 'components/LightConversation/LightChatHeader';
import { ClassicConversationHead } from 'views/Conversation/Chat/components/ClassicConversationHead';
import { useConversation } from 'hooks/business/lruConversation';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 180px);
  margin: -24px;
  background-color: #f0f2f5;
`;

const StyledChat = styled(Chat)`
  flex: 1 0 0;
  border-right: 1px solid #d3d7e0;
`;

const StyledSider = styled(ConversationSider)`
  flex: 0 0 420px;
  border-left: 1px solid #e1e4eb;
`;

export interface ConversationModalProps extends ModalProps {
  record: Pick<Remind.DetailRecord, 'conversationId' | 'buyerNick'>;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
  children?: (chat: JSX.Element, sider: JSX.Element) => JSX.Element;
}

interface MainContentProps {
  conversationId: Conversation.Conversation['conversationId'];
  forceAcquireControlReason: ConversationModalProps['forceAcquireControlReason'];
  children: (chat: JSX.Element, sider: JSX.Element) => JSX.Element;
  onRetry: () => void;
}

const MainContent = (props: MainContentProps) => {
  const { conversationId, forceAcquireControlReason, children, onRetry } = props;
  const conversation = useConversation(conversationId);
  const { t } = useTranslate();

  const isLightChatLayout = useIsLightChatLayout();

  if (!conversation) {
    return (
      <Container>
        <Result
          status="500"
          title={t('app_error_title')}
          subTitle={t('app_error_subTitle')}
          extra={
            <Button type="primary" onClick={onRetry}>
              {t('app_reload_now')}
            </Button>
          }
        />
      </Container>
    );
  }

  const side = isLightChatLayout ? (
    <LightChatSide
      className="basis-[420px] flex-shrink-0 flex-grow-0 border-0 border-l border-solid border-[#E1E4EB]"
      conversation={conversation}
    />
  ) : (
    <StyledSider conversation={conversation} />
  );

  const header = isLightChatLayout ? (
    <LightChatHeader
      className="h-[48px] flex-shrink-0 items-center flex border-0 border-b border-solid border-[#E1E4EB]"
      conversation={conversation}
    />
  ) : (
    <ClassicConversationHead conversation={conversation} />
  );

  return (
    <ChatConversationProvider conversationId={conversationId}>
      {children(
        <ChatErrorBoundary>
          <div className="h-full overflow-hidden flex flex-col flex-1">
            {header}
            <StyledChat
              conversationId={conversationId}
              forceAcquireControlReason={forceAcquireControlReason}
            />
          </div>
        </ChatErrorBoundary>,
        side,
      )}
    </ChatConversationProvider>
  );
};

export function ConversationModalContent(props: ConversationModalProps) {
  const {
    visible,
    record: { conversationId },
    forceAcquireControlReason,
    children = (chat, sider) => <>{[chat, sider]}</>,
  } = props;

  const { loading, retry } = useSecondConversation({
    conversationId,
    visible,
    forceAcquireControlReason,
  });

  if (loading) {
    return (
      <Container>
        <LoadingOutlined />
      </Container>
    );
  }

  return (
    <Container>
      <MainContent
        conversationId={conversationId}
        children={children}
        forceAcquireControlReason={forceAcquireControlReason}
        onRetry={() => retry(conversationId)}
      />
    </Container>
  );
}

function ConversationModal(props: ConversationModalProps) {
  const {
    record: { buyerNick },
    forceAcquireControlReason,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  return (
    <Modal
      width="90%"
      title={`${buyerNickMask(buyerNick)} ${t('remind.会话详情')}`}
      footer={null}
      centered
      destroyOnClose
      {...otherProps}
    >
      <ConversationModalContent {...props} />
    </Modal>
  );
}

export default ConversationModal;
